import { Component, OnDestroy, OnInit } from '@angular/core';
import { BackNavigationService } from '@irian-ro/shared-components-module';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subject } from 'rxjs';
import { AbstractContactContext, AbstractContactVO } from '@well/domain';
import {  switchMap, takeUntil } from 'rxjs/operators';
import { DelContactEmailableResourceService, NotifiableContactResourceService } from '@well/resource-services';

@Component({
  selector: 'well-abstract-notifiable-entity-contact-list-update-popup',
  templateUrl: './abstract-notifiable-entity-contact-list-update-popup.component.html',
  styleUrls: ['./abstract-notifiable-entity-contact-list-update-popup.component.scss'],
})
export class AbstractNotifiableEntityContactListUpdatePopupComponent implements OnInit, OnDestroy {
  selectedContactsUuids: string[] = [];
  contactList: AbstractContactVO[] | null = null;

  private context: AbstractContactContext | undefined = undefined;
  private abstractNotifiableEntityUuid: string | undefined = undefined;
  private businessProfileUuids: string[] | undefined = undefined;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private backNavigationService: BackNavigationService,
    private route: ActivatedRoute,
    private notifiableContactResourceService: NotifiableContactResourceService,
    private delContactResourceService: DelContactEmailableResourceService
  ) {}

  ngOnInit(): void {
    this.fetchContacts();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  private fetchContacts() {
    this.route.params
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          this.context = params['context'];
          this.abstractNotifiableEntityUuid = params['abstractNotifiableEntityUuid'];
          this.businessProfileUuids = params['businessProfileUuid'].split(',');

          const entityUuid = this.abstractNotifiableEntityUuid;
          const profileUuids = this.businessProfileUuids;
          const context = this.context as AbstractContactContext;

          if (!entityUuid || !profileUuids) {
            return of([[], []] as [AbstractContactVO[], AbstractContactVO[]]);
          }

          return forkJoin(
            profileUuids.length > 1
              ? this.delContactResourceService.findAllEmailableContactsForBusinessProfiles(
                  profileUuids,
                  context
                )
              : this.delContactResourceService.findAllEmailableContactsBasedOnBusinessProfile(
                  profileUuids[0],
                  context
                ),
            this.notifiableContactResourceService.getNotifiableContactsForEntityUuid(entityUuid)
          );
        })
      )
      .subscribe((res: [AbstractContactVO[], AbstractContactVO[]]) => {
        this.contactList = res[0];
        const missingContacts = res[1].filter(
          (existingEntityContact) =>
            this.contactList?.findIndex((contact) => contact.uuid === existingEntityContact.uuid) === -1
        );
        this.contactList.push(...missingContacts);
        this.selectedContactsUuids = res[1].map((i) => i.uuid).filter((uuid): uuid is string => !!uuid);
      });
  }

  onSubmitContacts() {
    if (!this.abstractNotifiableEntityUuid) {
      return;
    }
    this.notifiableContactResourceService
      .updateNotifiableContactsForEntityUuid(this.abstractNotifiableEntityUuid, this.selectedContactsUuids)
      .subscribe((res: any) => {
        this.backNavigationService.back({
          abstractNotifiableEntityUuid: this.abstractNotifiableEntityUuid,
          abstractContacts: res,
        });
      });
  }
}
